// frontend/src/modules/payroll/runStatus.ts
//
// One place that turns a payroll run's raw lifecycle into the label + tone the
// UI shows. Before `paid` existed every list page rolled its own three-branch
// ladder, so a new status rendered as a bare enum string ("paid") in some
// places and as "Cancelled" in others.
import type { StatusTone } from '../../design-system/visual';
import type { PayrollRun, PayrollRunStatus } from './types';

export interface RunStatusView {
  tone: StatusTone;
  label: string;
}

/** Committed = the payroll math is locked (finalized or paid). */
export const COMMITTED_RUN_STATUSES: readonly PayrollRunStatus[] = ['finalized', 'paid'];

export function isRunCommitted(run: Pick<PayrollRun, 'status'>): boolean {
  return COMMITTED_RUN_STATUSES.includes(run.status);
}

/** A paid run is frozen — no revert, no cancel, no component edits. */
export function isRunPaid(run: Pick<PayrollRun, 'status'>): boolean {
  return run.status === 'paid';
}

/**
 * Label + tone for a run. `paid` outranks `distributed` because it says the
 * money moved, not just that the payslip email went out.
 */
export function runStatusView(run: Pick<PayrollRun, 'status' | 'distributed_at'>): RunStatusView {
  switch (run.status) {
    case 'paid':
      return { tone: 'paid', label: 'Paid' };
    case 'finalized':
      return run.distributed_at
        ? { tone: 'success', label: 'Distributed' }
        : { tone: 'info', label: 'Finalized' };
    case 'cancelled':
      return { tone: 'muted', label: 'Cancelled' };
    case 'draft':
      return { tone: 'warning', label: 'Draft' };
    default:
      return { tone: 'muted', label: String(run.status) };
  }
}
